import React from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Button from "../../../components/ui/Button";
import { ModalConfig } from "../B2BLanding";

interface PricingTier {
  id: number;
  name: string;
  price: string;
  period: string;
  description: string;
  branding: string;
  features: string[];
  cta: string;
  highlight: boolean;
  modalConfig: Partial<ModalConfig>;
}

interface PricingSectionProps {
  onTierClick: (config: Partial<ModalConfig>) => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ onTierClick }) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const tiers: PricingTier[] = [
    {
      id: 0,
      name: "Cash (Ad-hoc)",
      price: "$699",
      period: "per stream",
      description: "Ideal for one-off services and independent homes.",
      branding: "Muzukuru Branding Only",
      features: [
        "1080p HD Streaming",
        "Digital Comment Book",
        "10% Sales Commission",
        "Basic Memorial Page",
        "No F.A.C.E. Portal Access",
      ],
      cta: "Book a Single Event",
      highlight: false,
      modalConfig: {
        title: "Book a Single Event",
        submitLabel: "Book Now",
        preSelectedTier: "cash",
      },
    },
    {
      id: 1,
      name: "Tier 1: Base",
      price: "$60,000",
      period: "per annum",
      description: "Professional partnership for growing assurance firms.",
      branding: "Co-Branded Experience",
      features: [
        "Unlimited High-Volume Streams",
        "50 Free Memorial Pages",
        "Optional F.A.C.E. Portal (+$10k)",
        "Digital Memento Access",
        "Partner Branding Support",
      ],
      cta: "Partner With Us",
      highlight: false,
      modalConfig: {
        title: "Partner With Us",
        submitLabel: "Let's Partner",
        preSelectedTier: "tier1",
      },
    },
    {
      id: 2,
      name: "Tier 2: Premium",
      price: "$100,000",
      period: "per annum",
      description: "The ultimate white-label digital transformation.",
      branding: "100% White-Label (Your Brand)",
      features: [
        "HD + Multi-Cam",
        "F.A.C.E. Portal Included",
        "Muzukuru Memento (Physical Books)",
        "Executive Data Dashboard",
        "Unlimited Memorial Pages",
      ],
      cta: "Go Enterprise",
      highlight: true,
      modalConfig: {
        title: "Go Enterprise",
        submitLabel: "Start Enterprise Journey",
        preSelectedTier: "tier2",
      },
    },
  ];

  return (
    <Box
      id="pricing"
      component="section"
      sx={{
        py: { xs: 12, md: 15 },
        backgroundColor: "rgba(255, 255, 255, 0.6)",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2rem" },
              fontWeight: 700,
              color: colors.primary[700],
              mb: 2,
              letterSpacing: "-0.05em",
              textTransform: "uppercase",
              textDecoration: "underline",
              textDecorationColor: colors.primary[400],
              textDecorationThickness: "2px",
              textUnderlineOffset: "8px",
            }}
          >
            Service Level Agreement Tiers
          </Typography>
          <Typography
            sx={{
              color: colors.primary[700],
              maxWidth: "800px",
              mx: "auto",
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            {"Scalable options designed to match your company's volume and branding requirements."}
          </Typography>
        </Box>

        {/* Pricing Cards Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 4,
          }}
        >
          {tiers.map((tier) => (
            <Paper
              key={tier.id}
              elevation={tier.highlight ? 8 : 2}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                bgcolor: colors.primary[900],
                borderRadius: 4,
                p: 4,
                border: tier.highlight
                  ? `1px solid ${colors.primary[400]}`
                  : `1px solid ${colors.primary[300]}`,
                transform: tier.highlight ? "scale(1.05)" : "scale(1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: tier.highlight
                    ? "translateY(-8px) scale(1.05)"
                    : "translateY(-8px)",
                },
              }}
            >
              {tier.highlight && (
                <Chip
                  label="RECOMMENDED FOR ENTERPRISE"
                  sx={{
                    position: "absolute",
                    top: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 700,
                    fontSize: "0.625rem",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    height: 32,
                  }}
                />
              )}

              {/* Tier Header */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: colors.primary[100],
                    mb: 1,
                  }}
                >
                  {tier.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "2.5rem",
                      fontWeight: 800,
                      color: colors.primary[100],
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {tier.price}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: colors.primary[300],
                      ml: 1,
                    }}
                  >
                    {tier.period}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: colors.primary[700],
                    fontWeight: 600,
                    fontStyle: "italic",
                  }}
                >
                  {tier.description}
                </Typography>
              </Box>

              {/* Branding Profile */}
              <Paper
                sx={{
                  mb: 4,
                  p: 2,
                  bgcolor: colors.primary[800],
                  borderRadius: 2,
                  boxShadow: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: colors.primary[400],
                    mb: 1,
                  }}
                >
                  Branding Profile
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.primary[100],
                    fontSize: "0.875rem",
                  }}
                >
                  {tier.branding}
                </Typography>
              </Paper>

              {/* Features List */}
              <List
                sx={{
                  flex: 1,
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {tier.features.map((feature, index) => (
                  <ListItem key={index} disableGutters sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: tier.highlight
                            ? colors.primary[400]
                            : colors.primary[300],
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "0.875rem",
                          color: colors.primary[100],
                          fontWeight: 600,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* CTA Button */}
              <Button
                fullWidth
                variant={tier.highlight ? "contained" : "outlined"}
                onClick={() => onTierClick(tier.modalConfig)}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  textTransform: "none",
                }}
              >
                {tier.cta}
              </Button>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PricingSection;